'use client';

import { useRef, useState } from 'react';

import type { realestatelst } from '@ps/models/lib/realestate-listing-page';
import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

import { useHorizontalAutoScroll } from '../hooks/use-horizontal-auto-scroll';

// ─── Constants ────────────────────────────────────────────────────────────

const GAP = 12;
/** Tailwind classes for the scroll container — height responsive: shorter on mobile. */
const CONTAINER_CLASSES = 'h-[360px] md:h-[588px]';
/** Tailwind classes for each column — narrower on mobile so multiple columns are visible. */
const COLUMN_CLASSES = 'w-[180px] md:w-[288px]';

/**
 * Repeating pattern of top-image flex ratios per column index.
 * The bottom image gets `1 - ratio`. This creates the alternating
 * short/tall zigzag effect.
 */
const ZIGZAG_TOP_RATIOS = [0.39, 0.53, 0.44, 0.58, 0.47, 0.39];

function getTopRatio(columnIndex: number): number {
  return ZIGZAG_TOP_RATIOS[columnIndex % ZIGZAG_TOP_RATIOS.length];
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/** Groups images into pairs (columns of 2). A trailing odd image gets its own column. */
function pairImages(
  images: realestatelst.GalleryImage[],
): realestatelst.GalleryImage[][] {
  const pairs: realestatelst.GalleryImage[][] = [];
  for (let index = 0; index < images.length; index += 2) {
    pairs.push(images.slice(index, index + 2));
  }
  return pairs;
}

// ─── Component ────────────────────────────────────────────────────────────

export function GalleryZigzag({
  images,
}: {
  images: realestatelst.GalleryImage[];
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const columns = pairImages(images);

  useHorizontalAutoScroll(scrollRef, {
    paused: isHovered || isViewerOpen,
  });

  // Scroll by ~2 columns per arrow click, using the live column width so this
  // adapts to the responsive sizing.
  const scrollBy = (direction: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const firstColumn = container.firstElementChild as HTMLElement | null;
    const columnWidth = firstColumn?.offsetWidth ?? 288;
    container.scrollBy({
      left: direction * (columnWidth + GAP) * 2,
      behavior: 'smooth',
    });
  };

  return (
    <PhotoProvider onVisibleChange={visible => setIsViewerOpen(visible)}>
      <div
        className="group/zigzag relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          ref={scrollRef}
          className={`scrollbar-hide flex gap-3 overflow-x-auto overflow-y-hidden rounded-[10px] ${CONTAINER_CLASSES}`}
        >
          {columns.map((pair, columnIndex) => {
            const topRatio = getTopRatio(columnIndex);
            return (
              <div
                key={pair[0].id}
                className={`flex h-full shrink-0 flex-col gap-3 ${COLUMN_CLASSES}`}
              >
                {pair.map((image, indexInPair) => {
                  const flexBasis =
                    pair.length === 1
                      ? 1
                      : indexInPair === 0
                        ? topRatio
                        : 1 - topRatio;

                  return (
                    <div
                      key={image.id}
                      className="min-h-0 overflow-hidden rounded-[10px] bg-gray-100 dark:bg-gray-800"
                      style={{ flex: flexBasis }}
                    >
                      {image.url ? (
                        <PhotoView src={image.url}>
                          <img
                            src={image.url}
                            alt={image.alt ?? 'Gallery image'}
                            className="h-full w-full cursor-zoom-in object-cover"
                            loading="lazy"
                          />
                        </PhotoView>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows */}
        {columns.length > 3 && (
          <>
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="absolute top-1/2 left-2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-1.5 opacity-0 shadow-md transition-opacity group-hover/zigzag:opacity-100 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-800"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-4 w-4 text-gray-700 dark:text-gray-300" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded-full bg-white/90 p-1.5 opacity-0 shadow-md transition-opacity group-hover/zigzag:opacity-100 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-800"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-4 w-4 text-gray-700 dark:text-gray-300" />
            </button>
          </>
        )}
      </div>
    </PhotoProvider>
  );
}
